import { useEffect, useMemo, useState } from 'react'
import type { Workspace } from '../types/workspace'
import type { ChecklistItem } from '../types/schema'
import type { StockItem } from '../types/stock'
import type { Quota } from '../types/quota'
import { subscribeWorkspaceStock } from '../services/stockService'
import { listActiveQuotas } from '../services/quotaService'
import { buildResourceRiskQueue, buildResourceSignals } from '../services/resourceAdapter'
import { isQuotaEnabled, isStockEnabled } from '../services/featureRegistry'

const EMPTY_STOCK: StockItem[] = []
const EMPTY_QUOTAS: Quota[] = []

export const useResourceReadiness = (
  workspace: Workspace | null | undefined,
  checklistItems: ChecklistItem[] = [],
  elements: Array<{ id: string; title?: string }> = [],
) => {
  const stockEnabled = Boolean(workspace && isStockEnabled(workspace))
  const quotaEnabled = Boolean(workspace && isQuotaEnabled(workspace))
  const workspaceId = workspace?.id

  const [subscribedStock, setSubscribedStock] = useState<{
    workspaceId: string
    items: StockItem[]
  } | null>(null)

  const [subscribedQuotas, setSubscribedQuotas] = useState<{
    workspaceId: string
    items: Quota[]
  } | null>(null)

  useEffect(() => {
    if (!workspaceId || !stockEnabled) {
      return
    }
    const currentWorkspaceId = workspaceId
    return subscribeWorkspaceStock(
      currentWorkspaceId,
      (items) => setSubscribedStock({ workspaceId: currentWorkspaceId, items }),
      () => setSubscribedStock({ workspaceId: currentWorkspaceId, items: EMPTY_STOCK }),
    )
  }, [stockEnabled, workspaceId])

  useEffect(() => {
    let cancelled = false
    if (!workspaceId || !quotaEnabled) {
      return () => { cancelled = true }
    }
    const currentWorkspaceId = workspaceId
    void listActiveQuotas(currentWorkspaceId)
      .then((next) => {
        if (!cancelled) setSubscribedQuotas({ workspaceId: currentWorkspaceId, items: next })
      })
      .catch(() => {
        if (!cancelled) setSubscribedQuotas({ workspaceId: currentWorkspaceId, items: EMPTY_QUOTAS })
      })
    return () => { cancelled = true }
  }, [quotaEnabled, workspaceId])

  const stockItems = useMemo(() => {
    if (!stockEnabled || !workspaceId) return EMPTY_STOCK
    if (subscribedStock?.workspaceId === workspaceId) return subscribedStock.items
    return EMPTY_STOCK
  }, [stockEnabled, subscribedStock, workspaceId])

  const quotas = useMemo(() => {
    if (!quotaEnabled || !workspaceId) return EMPTY_QUOTAS
    if (subscribedQuotas?.workspaceId === workspaceId) return subscribedQuotas.items
    return EMPTY_QUOTAS
  }, [quotaEnabled, subscribedQuotas, workspaceId])

  const riskQueue = useMemo(
    () => buildResourceRiskQueue({ workspace, checklistItems, elements, stockItems, quotas }),
    [workspace, checklistItems, elements, stockItems, quotas],
  )

  const getSignalsForNode = (node: ChecklistItem, completionStage = false) =>
    buildResourceSignals({ workspace, node, stockItems, quotas, completionStage })

  return { stockItems, quotas, riskQueue, getSignalsForNode }
}
